import { useState, useRef, useCallback, useEffect } from "react";
import { useShareGate } from "@/hooks/useShareGate";
import { ToolContainer, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CameraNotice } from "@/components/CameraNotice";
import { Camera, Crop, Copy, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { getMediaErrorMessage } from "@/lib/mediaPermissions";
import { MediaPermissionAlert } from "@/components/MediaPermissionAlert";

const TOOL_INFO_CONTENT = {
  what: `Takes a photo using your device's camera (webcam, front, or rear camera) directly in your browser — no separate camera app or upload step needed. Optionally crop the photo down to just the region you want after capturing. Runs entirely in your browser — nothing is uploaded anywhere.`,
  how: `Grant camera access, choose a camera if more than one is available, and click Capture. Drag a rectangle over the photo to crop it if you only need part of the frame, then download or copy the result.`,
  tip: `If your device has multiple cameras (front and rear on a phone, or an external webcam alongside a built-in one on a laptop), a dropdown to switch between them appears once camera access is granted — the first camera your browser lists isn't always the one you want.`,
};

interface SelectionRect { x: number; y: number; w: number; h: number; }
interface CameraDevice { deviceId: string; label: string; }

export default function CameraCapture() {
  const { gateShare } = useShareGate();
  const [streaming, setStreaming] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [devices, setDevices] = useState<CameraDevice[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>("");
  const [capturedImg, setCapturedImg] = useState<HTMLImageElement | null>(null);
  const [selection, setSelection] = useState<SelectionRect | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);
  const [streamKey, setStreamKey] = useState(0);
  const [facing, setFacing] = useState<"user" | "environment">("user");
  const [asJpg, setAsJpg] = useState(false);
  const [resolution, setResolution] = useState("");
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const displayImgRef = useRef<HTMLImageElement>(null);
  const pendingStreamRef = useRef<MediaStream | null>(null);

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setStreaming(false);
  }, []);

  useEffect(() => () => stopStream(), [stopStream]);

  const startCamera = async (deviceId?: string) => {
    setCameraError(null);
    try {
      const constraints: MediaStreamConstraints = {
        // Ask for the best the camera can do (the browser picks the closest it supports), not the 640x480 default.
        video: deviceId
          ? { deviceId: { exact: deviceId }, width: { ideal: 4096 }, height: { ideal: 2160 } }
          : { facingMode: facing, width: { ideal: 4096 }, height: { ideal: 2160 } },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      // The <video> element only exists in the DOM once streaming is
      // true -- at this point it hasn't rendered yet on the initial
      // start (videoRef.current would still be null), so the stream is
      // attached in the effect below instead, once React has actually
      // mounted the element.
      pendingStreamRef.current = stream;
      setStreaming(true);
      setStreamKey(k => k + 1); // a camera switch stops and restarts within one render: force the video to re-attach
      setCapturedImg(null);
      setCroppedBlob(null);

      // Camera labels are only populated by the browser after permission
      // is actually granted, so the device list is fetched here rather
      // than upfront.
      const allDevices = await navigator.mediaDevices.enumerateDevices();
      const cams = allDevices
        .filter(d => d.kind === "videoinput")
        .map((d, i) => ({ deviceId: d.deviceId, label: d.label || `Camera ${i + 1}` }));
      setDevices(cams);
      if (!selectedDeviceId && cams.length > 0) {
        const activeTrack = stream.getVideoTracks()[0];
        const activeId = activeTrack.getSettings().deviceId;
        setSelectedDeviceId(activeId || cams[0].deviceId);
      }
    } catch (err) {
      setCameraError(getMediaErrorMessage(err, "camera"));
    }
  };

  // Attaches the pending stream once the <video> element actually
  // exists -- runs right after streaming flips to true and React has
  // mounted the element, which is the earliest point videoRef.current
  // is guaranteed non-null.
  useEffect(() => {
    if (!streaming || !pendingStreamRef.current || !videoRef.current) return;
    const video = videoRef.current;
    const stream = pendingStreamRef.current;
    pendingStreamRef.current = null;
    video.srcObject = stream;
    video.onloadedmetadata = () => setResolution(`${video.videoWidth} x ${video.videoHeight}`);
    video.play().catch((playErr) => {
      console.error("Video playback failed:", playErr);
      setCameraError("Got camera access, but the preview couldn't start playing. Try again.");
      stream.getTracks().forEach(t => t.stop());
      setStreaming(false);
    });
  }, [streaming, streamKey]);

  const switchCamera = (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    stopStream();
    startCamera(deviceId);
  };

  // Front/rear toggle for phones, where devices are not always listed by name.
  const flipFacing = () => {
    const next = facing === "user" ? "environment" : "user";
    setFacing(next);
    stopStream();
    setSelectedDeviceId("");
    // startCamera reads `facing` from state; pass through a microtask so the new value is used.
    setTimeout(() => startCameraWith(next), 0);
  };
  const startCameraWith = async (f: "user" | "environment") => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: f }, width: { ideal: 4096 }, height: { ideal: 2160 } } });
      streamRef.current = stream; pendingStreamRef.current = stream;
      setStreaming(true); setStreamKey(k => k + 1); setCapturedImg(null); setCroppedBlob(null);
    } catch (err) { setCameraError(getMediaErrorMessage(err, "camera")); }
  };
  const stamp = () => { const d = new Date(); const z = (n: number) => String(n).padStart(2, "0"); return `camera-${d.getFullYear()}${z(d.getMonth() + 1)}${z(d.getDate())}-${z(d.getHours())}${z(d.getMinutes())}${z(d.getSeconds())}`; };

  const takePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      toast.error("The camera preview isn't ready yet — wait a moment for the live feed to appear, then try again.");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")!.drawImage(video, 0, 0);
    const img = new Image();
    img.onload = () => { setCapturedImg(img); setSelection(null); setCroppedBlob(null); };
    img.src = canvas.toDataURL("image/png");
    stopStream();
  };

  const getRelativeCoordsAt = (clientX: number, clientY: number): { x: number; y: number } | null => {
    const displayImg = displayImgRef.current;
    if (!displayImg || !capturedImg) return null;
    const rect = displayImg.getBoundingClientRect();
    const scaleX = capturedImg.naturalWidth / rect.width;
    const scaleY = capturedImg.naturalHeight / rect.height;
    return {
      x: Math.max(0, Math.min(capturedImg.naturalWidth, (clientX - rect.left) * scaleX)),
      y: Math.max(0, Math.min(capturedImg.naturalHeight, (clientY - rect.top) * scaleY)),
    };
  };
  // Thin wrapper kept so the existing mouse-event call sites below don't
  // need to change -- getRelativeCoordsAt is the one real implementation
  // both mouse and touch paths now share.
  const getRelativeCoords = (e: React.MouseEvent) => getRelativeCoordsAt(e.clientX, e.clientY);

  const onMouseDown = (e: React.MouseEvent) => {
    const coords = getRelativeCoords(e);
    if (!coords) return;
    setDragStart(coords);
    setSelection({ x: coords.x, y: coords.y, w: 0, h: 0 });
    setCroppedBlob(null);
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragStart) return;
    const coords = getRelativeCoords(e);
    if (!coords) return;
    setSelection({
      x: Math.min(dragStart.x, coords.x),
      y: Math.min(dragStart.y, coords.y),
      w: Math.abs(coords.x - dragStart.x),
      h: Math.abs(coords.y - dragStart.y),
    });
  };
  const onMouseUp = () => setDragStart(null);

  // Real touch support -- the same confirmed bug found and fixed in
  // Resize & Crop, Document Scanner, and Screen Snip: this drag-to-
  // select area only ever had mouse events, which touch interactions
  // on a phone or tablet don't reliably fire for an actual drag/swipe
  // gesture (only touchstart/touchmove/touchend do), so selecting a
  // crop region here simply never worked on a touch device -- despite
  // this tool's own core use case (taking a photo with the device
  // camera) being inherently mobile-first. e.preventDefault() on
  // touchmove stops the page scrolling out from under the drag.
  const onTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 0) return;
    const t = e.touches[0];
    const coords = getRelativeCoordsAt(t.clientX, t.clientY);
    if (!coords) return;
    setDragStart(coords);
    setSelection({ x: coords.x, y: coords.y, w: 0, h: 0 });
    setCroppedBlob(null);
  };
  const onTouchMove = (e: React.TouchEvent) => {
    if (!dragStart || e.touches.length === 0) return;
    e.preventDefault();
    const t = e.touches[0];
    const coords = getRelativeCoordsAt(t.clientX, t.clientY);
    if (!coords) return;
    setSelection({
      x: Math.min(dragStart.x, coords.x),
      y: Math.min(dragStart.y, coords.y),
      w: Math.abs(coords.x - dragStart.x),
      h: Math.abs(coords.y - dragStart.y),
    });
  };

  const applyCrop = () => {
    if (!capturedImg || !selection || selection.w < 2 || selection.h < 2) return;
    const canvas = document.createElement("canvas");
    canvas.width = selection.w;
    canvas.height = selection.h;
    canvas.getContext("2d")!.drawImage(capturedImg, selection.x, selection.y, selection.w, selection.h, 0, 0, selection.w, selection.h);
    canvas.toBlob(blob => { if (blob) setCroppedBlob(blob); }, asJpg ? "image/jpeg" : "image/png", 0.92);
  };

  const downloadFull = () => {
    if (!capturedImg) return;
    const c = document.createElement("canvas");
    c.width = capturedImg.naturalWidth; c.height = capturedImg.naturalHeight;
    c.getContext("2d")!.drawImage(capturedImg, 0, 0);
    c.toBlob(blob => { if (blob) downloadBlob(blob, `${stamp()}.${asJpg ? "jpg" : "png"}`); }, asJpg ? "image/jpeg" : "image/png", 0.92);
  };

  const copyToClipboard = (blob: Blob) => gateShare(async () => {
    try {
      // The clipboard only accepts PNG for images, so re-encode a JPEG capture first.
      let png = blob;
      if (blob.type !== "image/png") {
        const bmp = await createImageBitmap(blob);
        const c = document.createElement("canvas");
        c.width = bmp.width; c.height = bmp.height;
        c.getContext("2d")!.drawImage(bmp, 0, 0);
        png = await new Promise<Blob>((res, rej) => c.toBlob(b => (b ? res(b) : rej(new Error("encode"))), "image/png"));
      }
      await navigator.clipboard.write([new ClipboardItem({ "image/png": png })]);
      toast.success("Copied to clipboard!");
      return true;
    } catch {
      toast.error("Couldn't copy to clipboard in this browser — try downloading instead.");
      return false;
    }
  });

  return (
    <ToolContainer>
      {!streaming && !capturedImg && (
        <div className="py-16 border-2 border-dashed rounded-xl text-center space-y-3">
          <Camera className="h-12 w-12 mx-auto text-muted-foreground" />
          <CameraNotice purpose="photo" />
          <Button onClick={() => startCamera()} className="gap-2"><Camera className="h-4 w-4" /> Start Camera</Button>
          {cameraError && <div className="px-4 max-w-md mx-auto text-left"><MediaPermissionAlert message={cameraError} /></div>}
        </div>
      )}

      {streaming && (
        <div className="space-y-4 text-center">
          {devices.length > 1 && (
            <div className="max-w-xs mx-auto text-left">
              <Select value={selectedDeviceId} onValueChange={switchCamera}>
                <SelectTrigger><SelectValue placeholder="Choose a camera" /></SelectTrigger>
                <SelectContent>
                  {devices.map(d => <SelectItem key={d.deviceId} value={d.deviceId}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {resolution && <p className="text-xs text-muted-foreground">Camera resolution: {resolution}</p>}
          <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-96 rounded-lg border mx-auto" />
          <div className="flex gap-3 justify-center">
            <Button onClick={takePhoto} className="gap-2"><Camera className="h-4 w-4" /> Capture</Button>
            <Button variant="outline" onClick={flipFacing}>Front / rear camera</Button>
            <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={asJpg} onChange={e => setAsJpg(e.target.checked)} /> Save as JPG</label>
            <Button variant="outline" onClick={stopStream}>Cancel</Button>
          </div>
        </div>
      )}

      {capturedImg && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground text-center">Drag a rectangle to crop, or download the full photo as-is</p>
          <div
            className="relative inline-block mx-auto max-w-full cursor-crosshair select-none"
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onMouseUp}
            onTouchCancel={onMouseUp}
            style={{ touchAction: "none" }}
          >
            <img ref={displayImgRef} src={capturedImg.src} className="max-w-full max-h-[60vh] rounded-lg border block" draggable={false} />
            {selection && selection.w > 0 && (
              <div
                className="absolute border-2 border-primary bg-primary/20 pointer-events-none"
                style={{
                  left: `${(selection.x / capturedImg.naturalWidth) * 100}%`,
                  top: `${(selection.y / capturedImg.naturalHeight) * 100}%`,
                  width: `${(selection.w / capturedImg.naturalWidth) * 100}%`,
                  height: `${(selection.h / capturedImg.naturalHeight) * 100}%`,
                }}
              />
            )}
          </div>

          <div className="flex gap-3 justify-center flex-wrap">
            <DownloadButton onClick={downloadFull} markHint={`${stamp()}.${asJpg ? "jpg" : "png"}`} label="Download Full Photo" />
            <Button variant="outline" onClick={applyCrop} disabled={!selection || selection.w < 2} className="gap-2">
              <Crop className="h-3.5 w-3.5" /> Crop Selection
            </Button>
            <Button variant="outline" onClick={() => startCamera(selectedDeviceId)} className="gap-2">
              <RotateCcw className="h-3.5 w-3.5" /> Retake
            </Button>
          </div>

          {croppedBlob && (
            <div className="p-4 rounded-lg border bg-card space-y-3 text-center max-w-sm mx-auto">
              <img src={URL.createObjectURL(croppedBlob)} className="max-w-full rounded border mx-auto" />
              <div className="flex gap-3 justify-center">
                <DownloadButton blob={croppedBlob} fileName={`${stamp()}-cropped.${asJpg ? "jpg" : "png"}`} label="Download Crop" />
                <Button variant="outline" onClick={() => copyToClipboard(croppedBlob)} className="gap-2"><Copy className="h-3.5 w-3.5" /> Copy</Button>
              </div>
            </div>
          )}
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
